import { useQuery } from '@tanstack/react-query';
import { getContracts } from '@/features/contracts/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  TrendingUp,
  Calendar,
  Lock,
  ShieldAlert,
  ShieldCheck,
} from '@/lib/icons';
import type { EscalationLevelType } from '@/features/contracts/contract';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store/hooks';
import { formatCurrency } from '@/lib/utils';
import { ListFilterBar, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { formatDate } from '@/lib/dates';

type EscalationFilter = 'warning' | 'partial_lock' | 'full_lock' | 'defaulted';

const ESCALATION_FILTER_LABELS: Record<EscalationFilter, string> = {
  warning: 'Warning',
  partial_lock: 'Partial Lock',
  full_lock: 'Locked',
  defaulted: 'Defaulted',
};

const escalationBadge = (level: EscalationLevelType, daysOverdue: number) => {
  if (level === 'none') {
    return (
      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400">
        <ShieldCheck className="mr-1 h-3 w-3" />
        Healthy
      </Badge>
    );
  }
  const map: Record<
    EscalationFilter,
    { cls: string; label: string; icon: React.ReactNode }
  > = {
    warning: {
      cls: 'bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-400',
      label: 'Warning',
      icon: <AlertTriangle className="mr-1 h-3 w-3" />,
    },
    partial_lock: {
      cls: 'bg-orange-500/15 text-orange-700 border-orange-500/30 dark:text-orange-400',
      label: 'Partial',
      icon: <ShieldAlert className="mr-1 h-3 w-3" />,
    },
    full_lock: {
      cls: 'bg-red-500/15 text-red-700 border-red-500/30 dark:text-red-400',
      label: 'Locked',
      icon: <Lock className="mr-1 h-3 w-3" />,
    },
    defaulted: {
      cls: 'bg-rose-600/15 text-rose-700 border-rose-600/30 dark:text-rose-300',
      label: 'Defaulted',
      icon: <XCircle className="mr-1 h-3 w-3" />,
    },
  };
  const cfg = map[level as EscalationFilter];
  if (!cfg) return null;
  return (
    <div className="flex items-center gap-1">
      <Badge className={cfg.cls}>
        {cfg.icon}
        {cfg.label}
      </Badge>
      {daysOverdue > 0 && (
        <span className="text-[10px] font-mono tabular-nums text-muted-foreground">
          {daysOverdue}d
        </span>
      )}
    </div>
  );
};

const CONTRACT_STATUS_VALUES = [
  'draft',
  'pending_payment',
  'active',
  'completed',
  'defaulted',
  'cancelled',
] as const;

const contractsSearchSchema = z.object({
  escalation: z
    .enum(['warning', 'partial_lock', 'full_lock', 'defaulted'])
    .optional(),
  q: z.string().optional(),
  branchId: z.string().optional(),
  status: z.enum(CONTRACT_STATUS_VALUES).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/contracts/')({
  component: ContractsPage,
  validateSearch: contractsSearchSchema,
});

const statusBadge = (status: string) => {
  switch (status) {
    case 'active':
      return (
        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    case 'defaulted':
      return (
        <Badge className="bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400">
          <XCircle className="w-3 h-3 mr-1" />
          Defaulted
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400">
          <XCircle className="w-3 h-3 mr-1" />
          Cancelled
        </Badge>
      );
    case 'draft':
      return (
        <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400">
          <Calendar className="w-3 h-3 mr-1" />
          Draft
        </Badge>
      );
    case 'pending_payment':
      return (
        <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400">
          <Calendar className="w-3 h-3 mr-1" />
          Pending Payment
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const PAGE_SIZE = 20;

function ContractsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search_params = Route.useSearch();
  const escalationFilter = search_params.escalation;
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount,
  } = useListFilters({ search: search_params, to: '/contracts' });

  const page = search_params.page ?? 1;

  // Backend computes the escalation snapshot per contract (via the lock-engine
  // thresholds) and now filters on branch and free text as well.
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [
      'contracts',
      debouncedSearch,
      search_params.status,
      search_params.branchId,
      escalationFilter,
    ],
    queryFn: () =>
      getContracts({
        search: debouncedSearch,
        contractStatus: search_params.status,
        branchId: search_params.branchId,
        escalation: escalationFilter,
      }),
    placeholderData: (previous) => previous,
  });

  const allContracts = data?.value ?? [];

  const clearEscalation = () => setFilters({ escalation: undefined });
  const totalPages = Math.max(1, Math.ceil(allContracts.length / PAGE_SIZE));
  const contracts = allContracts.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
  const startItem = allContracts.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, allContracts.length);
  const active = allContracts.filter((c) => c.status === 'active').length;
  const completed = allContracts.filter((c) => c.status === 'completed').length;
  const defaulted = allContracts.filter((c) => c.status === 'defaulted').length;
  const totalValue = allContracts.reduce((s, c) => s + c.totalAmount, 0);

  const stats = [
    {
      label: 'Active Contracts',
      value: active,
      icon: FileText,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: 'Completed',
      value: completed,
      icon: CheckCircle,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Defaulted',
      value: defaulted,
      icon: AlertTriangle,
      color: 'from-red-500 to-red-600',
    },
    {
      label: 'Total Value',
      value: formatCurrency(totalValue, currency),
      icon: TrendingUp,
      color: 'from-violet-500 to-violet-600',
    },
  ];

  const CHIP_LABELS: Record<(typeof CONTRACT_STATUS_VALUES)[number], string> = {
    draft: 'Draft',
    pending_payment: 'Pending Payment',
    active: 'Active',
    completed: 'Completed',
    defaulted: 'Defaulted',
    cancelled: 'Cancelled',
  };

  const chips: ReadonlyArray<
    FilterChip<'all' | (typeof CONTRACT_STATUS_VALUES)[number]>
  > = [
    { value: 'all', label: 'All' },
    ...CONTRACT_STATUS_VALUES.map((value) => ({
      value,
      label: CHIP_LABELS[value],
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contracts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage financing contracts, track payments, and monitor defaults.
            Contracts are generated automatically when an application is
            approved.
          </p>
        </div>
      </div>

      {/* Escalation filter chip (when set via ?escalation= query) */}
      {escalationFilter && (
        <div className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/10 px-4 py-2.5">
          <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-orange-700 dark:text-orange-400">
              Filtered by escalation:
            </span>{' '}
            <span className="text-foreground">
              {ESCALATION_FILTER_LABELS[escalationFilter]}
            </span>{' '}
            <span className="text-muted-foreground">
              ({allContracts.length})
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearEscalation}
            className="h-7"
          >
            <XCircle className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-in">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md card-shine"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilterBar
        chips={chips}
        chipValue={search_params.status ?? 'all'}
        onChipChange={(value) =>
          setFilters({ status: value === 'all' ? undefined : value })
        }
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.contracts')}
        isSearchPending={isSearchPending}
        branchId={search_params.branchId}
        onBranchChange={(branchId) => setFilters({ branchId })}
        activeCount={activeCount}
        onReset={reset}
        isPending={isFetching}
      />

      {/* Table */}
      <Card className="shadow-md border-0">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title="Failed to load contracts"
              />
            </div>
          ) : allContracts.length === 0 ? (
            <NoFilterResults
              isFiltered={activeCount > 0 || Boolean(escalationFilter)}
              emptyTitle="No contracts yet"
              emptyDescription="Contracts are generated automatically when an application is approved."
              icon={FileText}
              onReset={reset}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden md:table-cell">
                      Device
                    </TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Monthly
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Escalation</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Frequency
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      Overdue
                    </TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Next Payment
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map((c) => (
                    <TableRow
                      key={c.id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer row-hover-lift"
                      onClick={() =>
                        navigate({
                          to: '/contracts/$contractId',
                          params: { contractId: c.id },
                        })
                      }
                    >
                      <TableCell className="font-medium">
                        <div>{c.customerName}</div>
                        {c.customerPhone && (
                          <div className="text-xs text-muted-foreground">
                            {c.customerPhone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden md:table-cell">
                        {c.deviceName}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(c.totalAmount, currency)}
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden lg:table-cell">
                        {formatCurrency(c.monthlyPayment, currency)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          {statusBadge(c.status)}
                          {c.writtenOffAt && (
                            <Badge variant="outline" className="text-xs">
                              {t('contracts.writtenOff.badge')}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {escalationBadge(c.escalationLevel, c.daysOverdue)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {c.installmentFrequency ? (
                          <Badge
                            className={
                              c.installmentFrequency === 'daily'
                                ? 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400'
                                : c.installmentFrequency === 'weekly'
                                  ? 'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400'
                                  : 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400'
                            }
                          >
                            {c.installmentFrequency.charAt(0).toUpperCase() +
                              c.installmentFrequency.slice(1)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            -
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {c.overdueAmount > 0 ? (
                          <span className="text-red-600 dark:text-red-400 font-semibold">
                            {formatCurrency(c.overdueAmount, currency)}
                          </span>
                        ) : (
                          <span className="text-emerald-600 dark:text-emerald-400 text-sm">
                            None
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm hidden lg:table-cell">
                        {c.nextPaymentDate
                          ? formatDate(c.nextPaymentDate)
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {allContracts.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {startItem}-{endItem} of {allContracts.length}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFilters({ page: Math.max(1, page - 1) })}
                  className={
                    page <= 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    isActive={page === i + 1}
                    onClick={() => setFilters({ page: i + 1 })}
                    className="cursor-pointer"
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setFilters({ page: Math.min(totalPages, page + 1) })}
                  className={
                    page >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
